import { useMemo, useState } from "react";
import {
	Box,
	Button,
	Stack,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from "@mui/material";
import { useUserStore } from "../../stores/user.store";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import {
	useApproveEventInstance,
	useRejectEventInstance,
} from "../../hooks/useEventInstance";
import { ReviewDetail } from "@/domain/eventInstance.interface";
import {
	CompoundView,
	FormDisplay,
	GenericPlaceholder,
	PhotoViewer,
	SubmissionDetailsModal,
	VideoPlayer,
} from "../../components/SubmissionViewer";
import { ApproveDialog } from "./ApproveDialog";
import { RejectDialog } from "./RejectDialog";

interface Props {
	open: boolean;
	enrollmentId: string | undefined;
	instanceId: string | undefined;
	onClose: () => void;
}

const renderStepBody = (
	detail: ReviewDetail,
	stepIndex: number,
): JSX.Element => {
	const compound = detail.compoundSteps;
	if (compound?.length) {
		const step = compound[stepIndex];
		switch (step.validationType) {
			case "PHOTOS":
				return <PhotoViewer photos={step.photos ?? []} mode="review" />;
			case "FORM":
				return (
					<FormDisplay
						formConfig={step.formConfig}
						responses={step.formResponses}
						mode="review"
					/>
				);
			case "WATCH_VIDEO":
			case "CAPTURE_VIDEO":
				return <VideoPlayer mode="review" />;
			default:
				return (
					<GenericPlaceholder
						validationType={step.validationType}
						mode="review"
					/>
				);
		}
	}

	switch (detail.validationType) {
		case "PHOTOS":
			return <PhotoViewer photos={detail.photos ?? []} mode="review" />;
		case "FORM":
			return (
				<FormDisplay
					formConfig={detail.formConfig}
					responses={detail.formResponses}
					mode="review"
				/>
			);
		case "WATCH_VIDEO":
		case "CAPTURE_VIDEO":
			return <VideoPlayer mode="review" />;
		case "COMPOUND":
			// Defensive — compound but no resolved steps came through.
			return <CompoundView steps={detail.compoundSteps ?? []} mode="review" />;
		default:
			return (
				<GenericPlaceholder
					validationType={detail.validationType}
					mode="review"
				/>
			);
	}
};

export const ReviewModal = ({
	open,
	enrollmentId,
	instanceId,
	onClose,
}: Props) => {
	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const reviewerId = userInfo?.id ?? "";

	const [stepIndex, setStepIndex] = useState(0);
	const [approveOpen, setApproveOpen] = useState(false);
	const [rejectOpen, setRejectOpen] = useState(false);

	const approveMutation = useApproveEventInstance(reviewerId);
	const rejectMutation = useRejectEventInstance(reviewerId);

	const handleClose = (): void => {
		setStepIndex(0);
		setApproveOpen(false);
		setRejectOpen(false);
		onClose();
	};

	return (
		<>
			<SubmissionDetailsModal
				open={open}
				enrollmentId={enrollmentId}
				instanceId={instanceId}
				onClose={handleClose}
				renderBody={(detail) => (
					<ReviewBody
						detail={detail}
						stepIndex={stepIndex}
						onStep={setStepIndex}
					/>
				)}
				renderActions={(detail) => {
					const stepCount = detail.compoundSteps?.length ?? 1;
					const onLastStep = stepIndex >= stepCount - 1;
					return (
						<Stack
							direction="row"
							spacing={1}
							justifyContent="space-between"
							sx={{
								p: 2,
								borderTop: "1px solid",
								borderColor: "divider",
							}}
						>
							<Button
								color="error"
								variant="outlined"
								onClick={() => setRejectOpen(true)}
							>
								Reject
							</Button>
							<Stack direction="row" spacing={1}>
								{stepCount > 1 && (
									<Button
										onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
										disabled={stepIndex === 0}
									>
										← Previous
									</Button>
								)}
								{stepCount > 1 && !onLastStep && (
									<Button
										variant="contained"
										onClick={() =>
											setStepIndex((i) => Math.min(stepCount - 1, i + 1))
										}
									>
										Next →
									</Button>
								)}
								{onLastStep && (
									<Button
										variant="contained"
										color="success"
										onClick={() => setApproveOpen(true)}
									>
										Approve
									</Button>
								)}
							</Stack>
							<ApproveDialog
								open={approveOpen}
								eventName={detail.eventName}
								loading={approveMutation.isPending}
								onClose={() => setApproveOpen(false)}
								onConfirm={(notes) => {
									if (!enrollmentId || !instanceId) return;
									approveMutation.mutate(
										{
											enrollmentId,
											instanceId,
											body: notes ? { reviewNotes: notes } : {},
										},
										{
											onSuccess: () => {
												showSnackBar(
													`${detail.eventName ?? "Submission"} approved`,
													"success",
												);
												handleClose();
											},
											onError: (err) => {
												showSnackBar(
													(err as Error).message ?? "Approval failed",
													"error",
												);
											},
										},
									);
								}}
							/>
							<RejectDialog
								open={rejectOpen}
								eventName={detail.eventName}
								loading={rejectMutation.isPending}
								onClose={() => setRejectOpen(false)}
								onConfirm={(reason) => {
									if (!enrollmentId || !instanceId) return;
									rejectMutation.mutate(
										{
											enrollmentId,
											instanceId,
											body: { rejectionReason: reason },
										},
										{
											onSuccess: () => {
												showSnackBar(
													`${detail.eventName ?? "Submission"} rejected`,
													"success",
												);
												handleClose();
											},
											onError: (err) => {
												showSnackBar(
													(err as Error).message ?? "Rejection failed",
													"error",
												);
											},
										},
									);
								}}
							/>
						</Stack>
					);
				}}
			/>
		</>
	);
};

const ReviewBody = ({
	detail,
	stepIndex,
	onStep,
}: {
	detail: ReviewDetail;
	stepIndex: number;
	onStep: (index: number) => void;
}) => {
	const stepCount = detail.compoundSteps?.length ?? 1;
	const stepLabels = useMemo(() => {
		if (!detail.compoundSteps?.length)
			return [detail.eventName ?? "Submission"];
		return detail.compoundSteps.map((s, i) => s.name || `Step ${i + 1}`);
	}, [detail.compoundSteps, detail.eventName]);

	return (
		<Box>
			{stepCount > 1 && (
				<Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
					<Stepper activeStep={stepIndex} alternativeLabel>
						{stepLabels.map((label, idx) => (
							<Step
								key={`${label}-${idx}`}
								onClick={() => onStep(idx)}
								sx={{ cursor: "pointer" }}
							>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
					<Typography variant="caption" color="text.secondary">
						Step {stepIndex + 1} of {stepCount}: {stepLabels[stepIndex]}
					</Typography>
				</Box>
			)}
			{renderStepBody(detail, stepIndex)}
		</Box>
	);
};
